import React, { useState } from 'react';

import Table from '~components/molecules/Table';
import ActionButton from '~components/atoms/ActionButton';
import Text from '~components/atoms/Text';

import combinedActions from '~redux/combinedActions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import {
    CustomerIdTypeEnum,
    FundTypeEnum,
    GoldTypeEnum,
    ModuleEnum,
    PermissionsEnum,
    SilverTypeEnum,
    TransactionActionEnum,
    TransactionStatusEnum,
} from '~utils/enums';
import { TTransactionListing } from '~types/feat/gold';
import { TUpdateTransactionStatusReduxParams } from '~types/feat/approval';

import UtilFunctions from '~utils/functions';
import * as Styles from './_goldTransactionTable.module.scss';
import GoldTransactionProductDetailsTable from '../GoldTransactionProductDetailsTable';
import gold from '~sagas/gold';

const GoldTransactionTable = () => {
    const dispatch = useAppDispatch();

    const {
        Misc: {
            renderTransactionStatusChip,
            renderBinaryStatusChip,
            fixedDecimal,
            dateTimeFormat,
        },
    } = UtilFunctions;

    const [transactionModalMode, setTransactionModalMode] = useState<'view' | 'earmark' | 'edit'>('view');

    const isTransactionProductListingModalIsOpen = useAppSelector(state => state.gold.modals.transactionProductListingModalIsOpen);

    const transactionListing = useAppSelector(state => state.gold.buyAndSellGold.transactionListing.data);
    const getTransactionListingAttempt = useAppSelector(state => state.gold.loadings.getTransactionListing);

    const editAccessModules = useAppSelector(state => state.auth.userDetails.access).find(access => access.permission === PermissionsEnum.Edit)?.modules || [];

    const setTransactionProductListingModalIsOpen = (state: boolean) => dispatch(combinedActions.goldSetTransactionProductListingModalIsOpen(state));
    const setSelectedGoldTransaction = (transaction: TTransactionListing) => dispatch(combinedActions.goldSetSelectedTransaction(transaction));

    const setConfirmUpdateStatusModalIsOpen = (open: boolean) => dispatch(combinedActions.goldSetConfirmUpdateTransactionStatusModalIsOpen(open));
    const setTempUpdateTransactionState = (data: TUpdateTransactionStatusReduxParams) => dispatch(combinedActions.goldSetTempUpdateTransactionState(data));

    const tableHeader = [
        'TRANSACTION ID',
        'TRANSACTION TYPE',
        'EARMARK TRANSACTION',
        'PROJECT NAME',
        'CUSTOMER NAME',
        'CUSTOMER ID TYPE',
        'CUSTOMER ID NUMBER',
        'CIF NUMBER',
        'BRANCH CODE',
        'BRANCH NAME',
        'REGION NAME',
        'STATUS',
        'FUND TYPE',
        'GOLD TYPE',
        'SILVER TYPE',
        'BRANCH TELLER NAME',
        'SALES PERSONNEL NAME',
        'REFERRAL NAME',
        'TRANSACTION DATE',
        'ACTION',
    ];

    const handleViewTransaction = (transaction: TTransactionListing) => {
        setSelectedGoldTransaction(transaction);
        setTransactionModalMode('view');
        setTransactionProductListingModalIsOpen(true);
    };

    const handleEarmarkTransaction = (transaction: TTransactionListing) => {
        setSelectedGoldTransaction(transaction);
        setTransactionModalMode('earmark');
        setTransactionProductListingModalIsOpen(true);
    };

    const handleEditEarmarkTransaction = (transaction: TTransactionListing) => {
        setSelectedGoldTransaction(transaction);
        setTransactionModalMode('edit');
        setTransactionProductListingModalIsOpen(true);
    };

    const handleCancelTransactionClick = (data: TUpdateTransactionStatusReduxParams) => {
        setConfirmUpdateStatusModalIsOpen(true);
        setTempUpdateTransactionState(data);
    };

    let prevTransactionId = 0;
    const tableBody = transactionListing.map(transaction => {
        const {
            transactionId,
            isEarmarkTransaction,
            projectName,
            name,
            idType,
            idNo,
            CIFNo,
            branchCode,
            branchName,
            regionName,
            status,
            action,
            fundType,
            goldType,
            silverType,
            createdAt,
            branchTellerName,
            salesPersonnelName,
            referralName,
        } = transaction;

        const renderTransactionId = () => {
            if (prevTransactionId !== transactionId){
                prevTransactionId = transactionId;
                return transactionId;
            }

            return '-';
        };

        const renderAction = () => {
            if (!action) return '-';

            return TransactionActionEnum[action];
        };

        const metalType =
            goldType ? 'gold' :
                silverType ? 'silver' :
                    '';

        return {
            className:
                metalType === 'gold'
                    ? Styles['gold-row']
                    : '',

            cells: [
                renderTransactionId(),
                (
                    <Text
                        key={transactionId}
                        label={renderAction() || ''}
                        className={Styles['transaction-action-label']}
                        isBold
                    />
                ),
                isEarmarkTransaction ? renderBinaryStatusChip(isEarmarkTransaction, 'Yes') : '-',
                projectName ? projectName : '-',
                name,
                idType ? CustomerIdTypeEnum[idType] : '-',
                idNo,
                CIFNo,
                branchCode,
                branchName,
                regionName,
                renderTransactionStatusChip(status, TransactionStatusEnum[status]),
                fundType ? FundTypeEnum[fundType] : '-',
                goldType ? GoldTypeEnum[goldType].split(/(?=[A-Z])/).join(' ') : '-',
                silverType ? SilverTypeEnum[silverType].split(/(?=[A-Z])/).join(' ') : '-',
                branchTellerName || '-',
                salesPersonnelName || '-',
                referralName || '-',
                dateTimeFormat(createdAt),
                (
                    <div key={`action-${transactionId}`} className={Styles['action-button-container']}>
                        <div className={Styles['slot-left']}>
                            <ActionButton
                                label='View Details'
                                onClick={() => handleViewTransaction(transaction)}
                            />
                        </div>

                        <div className={Styles['slot-middle']}>
                            {(
                                goldType === GoldTypeEnum.PaperGold ||
                                silverType === SilverTypeEnum.PaperSilver
                            ) &&
                                !isEarmarkTransaction &&
                                status === TransactionStatusEnum.Approved && (
                                <ActionButton
                                    label='Convert to Earmark'
                                    onClick={() => handleEarmarkTransaction(transaction)}
                                />
                            )}

                            {isEarmarkTransaction &&
                                (
                                    (status === TransactionStatusEnum.Approved &&
                                        transaction.earmarkEndDate &&
                                        new Date(transaction.earmarkEndDate) >= new Date()) ||
                                    (status !== TransactionStatusEnum.Approved &&
                                        status !== TransactionStatusEnum.Rejected &&
                                        (!transaction.earmarkEndDate || new Date(transaction.earmarkEndDate) >= new Date()))
                                ) && (
                                <ActionButton
                                    label='Edit'
                                    onClick={() => handleEditEarmarkTransaction(transaction)}
                                />
                            )}
                        </div>

                        <div className={Styles['slot-right']}>
                            {editAccessModules.find(module => module === ModuleEnum.BuySell) &&
                                status === TransactionStatusEnum.Approved && (
                                <ActionButton
                                    className={Styles['cancel-transaction-button']}
                                    label='Cancel Transaction'
                                    onClick={() =>
                                        handleCancelTransactionClick({
                                            transactionId,
                                            status: TransactionStatusEnum.Cancelled,
                                        })
                                    }
                                />
                            )}
                        </div>
                    </div>
                ),
            ],
        };
    });

    return (
        <>
            <Table
                header={tableHeader}
                body={tableBody}
                isLoading={getTransactionListingAttempt}
            />

            <GoldTransactionProductDetailsTable
                isOpen={isTransactionProductListingModalIsOpen}
                onClose={() => setTransactionProductListingModalIsOpen(false)}
                isLoading={getTransactionListingAttempt}
                mode={transactionModalMode}
            />
        </>
    );
};

export default GoldTransactionTable;
